// ./src/hooks/usePlaceLocation.ts

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { PlaceTypeValue, SavedPlace } from "@/utils/api";
import { formatAddressShort } from "@/utils";
import { useSavedPlacesContext } from "@/contexts";
import { useAutoLocation } from "@/hooks/useAutoLocation";
import { GeoCoords } from "@/hooks/useGeolocation";
import { PlaceData, AddressData } from "@/hooks/useGoogleMapsAutocomplete";

// ============================================
// Types (unchanged)
// ============================================

export interface PlaceInitialValues {
  whereDone?: string;
  location?: string;
  addressData?: AddressData | null;
  latitude?: number | null;
  longitude?: number | null;
  savedPlaceId?: string | null;
  pointType?: string | null | undefined;
}

export interface UsePlaceLocationOptions {
  enabled: boolean;
  autoLocate?: boolean;
  initialValues?: PlaceInitialValues;
  travelType?: string;
  includePointType?: boolean;
  onFieldChange?: () => void;
}

export interface PlaceFieldsForInput {
  whereDone?: string;
  location?: string;
  address1?: string;
  address2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  countryId?: string;
  latitude?: number | null;
  longitude?: number | null;
  savedPlaceId?: string | null;
  savePlace?: boolean;
  pointType?: string;
}

interface PlaceLocationState {
  whereDone: string;
  location: string;
  addressData: AddressData | null;
  latitude: number | null;
  longitude: number | null;
  savedPlaceId: string | null;
  savePlace: boolean;
  pointType: string | null;
}

export interface UsePlaceLocationReturn {
  whereDone: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
  savedPlaceId: string | null;
  savePlace: boolean;
  pointType: string | null;
  locationBias: GeoCoords | null;
  savedPlacesAsPlaceData: PlaceData[];
  isLocationLoading: boolean;
  canSavePlace: boolean;
  handleWhereDoneChange: (name: string, placeData: PlaceData | null) => void;
  handleLocationChange: (address1: string, fullAddress: AddressData | null) => void;
  handleSavePlaceChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePointTypeChange: (name: string, value: PlaceTypeValue | null) => void;
  getPlaceFields: () => PlaceFieldsForInput;
  reset: (initialValues?: PlaceInitialValues) => void;
}

// ============================================
// Helper Functions (unchanged)
// ============================================

export const savedPlaceToPlaceData = (place: SavedPlace): PlaceData => {
  const address: AddressData = {
    addressId: place.id || "",
    displayName: place.whereDone || place.name || null,
    address1: place.address1 || null,
    address2: place.address2 || null,
    city: place.city || null,
    country: place.countryId || null,
    latitude: place.latitude ?? null,
    longitude: place.longitude ?? null,
    postalCode: place.postalCode || null,
    region: place.stateProvince || null,
    placeType: place.placeType || null,
  };

  return {
    placeId: "",
    savedPlaceId: place.id,
    name: place.name || "",
    address,
  };
};

const buildInitialState = (initialValues?: PlaceInitialValues): PlaceLocationState => ({
  whereDone: initialValues?.whereDone || "",
  location: initialValues?.location || "",
  addressData: initialValues?.addressData || null,
  latitude: initialValues?.latitude ?? null,
  longitude: initialValues?.longitude ?? null,
  savedPlaceId: initialValues?.savedPlaceId ?? null,
  savePlace: false,
  pointType: initialValues?.pointType ?? null,
});

const mapAddressToBackendFields = (
  addressData: AddressData
): Pick<PlaceFieldsForInput, "address1" | "address2" | "city" | "stateProvince" | "postalCode" | "countryId"> => ({
  address1: addressData.address1 || undefined,
  address2: addressData.address2 || undefined,
  city: addressData.city || undefined,
  stateProvince: addressData.region || undefined,
  postalCode: addressData.postalCode || undefined,
  countryId: addressData.country || undefined,
});

// ============================================
// Hook Implementation
// ============================================

export const usePlaceLocation = (options: UsePlaceLocationOptions): UsePlaceLocationReturn => {
  const {
    enabled,
    autoLocate = true,
    initialValues,
    travelType,
    includePointType = false,
    onFieldChange,
  } = options;

  const { savedPlaces: allSavedPlaces, listByProximity, getSortedByRelevance } =
    useSavedPlacesContext();

  const {
    coords: autoCoords,
    address: autoAddress,
    isReady: isLocationReady,
    // FIX 5 (redundant guard): useAutoLocation is called with
    // `enabled: enabled && autoLocate`, so when autoLocate is false the hook
    // is disabled and isLoading will never be true. The `&& autoLocate` guard
    // in the return value is therefore unnecessary. We keep the raw value here
    // and remove the guard below so the return value is always accurate.
    isLoading: isLocationLoading,
  } = useAutoLocation({
    enabled: enabled && autoLocate,
    reverseGeocode: true,
  });

  const [state, setState] = useState<PlaceLocationState>(() =>
    buildInitialState(initialValues)
  );
  const [locationBias, setLocationBias] = useState<GeoCoords | null>(null);

  // ── One-shot guards ──
  const hasAutoFilled = useRef(false);
  const hasProximityLookedUp = useRef(false);
  const [initialCoords, setInitialCoords] = useState<GeoCoords | null>(null);
  const hasInitialCoordsProximityLookedUp = useRef(false);

  // ── Stable onFieldChange ref ──
  const onFieldChangeRef = useRef(onFieldChange);
  onFieldChangeRef.current = onFieldChange;

  const notifyChange = useCallback(() => {
    onFieldChangeRef.current?.();
  }, []);

  // ============================================
  // Reset one-shot guards when enabled transitions false → true.
  // Declared before proximity lookup effects so React runs it first.
  // ============================================

  const prevEnabledRef = useRef(false);

  useEffect(() => {
    if (enabled && !prevEnabledRef.current) {
      hasAutoFilled.current = false;
      hasProximityLookedUp.current = false;
      hasInitialCoordsProximityLookedUp.current = false;
    }
    prevEnabledRef.current = enabled;
  }, [enabled]);

  // ============================================
  // Saved Places
  // ============================================

  const savedPlacesAsPlaceData = useMemo(() => {
    const sorted = getSortedByRelevance(travelType);
    return sorted.map(savedPlaceToPlaceData);
  }, [getSortedByRelevance, travelType]);

  // ============================================
  // Lifecycle: reset
  // ============================================

  const reset = useCallback((newInitialValues?: PlaceInitialValues) => {
    const newState = buildInitialState(newInitialValues);
    setState(newState);

    // Note: guard refs are reset by the enabled transition effect above.
    // reset() only handles form values and coordinates.

    if (newInitialValues?.latitude != null && newInitialValues?.longitude != null) {
      const coords: GeoCoords = {
        latitude: newInitialValues.latitude,
        longitude: newInitialValues.longitude,
      };
      setLocationBias(coords);
      setInitialCoords(coords);
    } else {
      setLocationBias(null);
      setInitialCoords(null);
    }
  }, []);

  // ============================================
  // Auto-location bias
  // ============================================

  useEffect(() => {
    if (autoCoords && enabled && autoLocate) {
      setLocationBias(autoCoords);
    }
  }, [autoCoords, enabled, autoLocate]);

  // ============================================
  // FIX 1: runProximityLookup dependency changed from allSavedPlaces.length
  // to allSavedPlaces (the full array reference).
  //
  // allSavedPlaces.length does not change when the contents of the list
  // change (e.g. a place is updated, the list loads asynchronously after
  // mount). Using the array reference itself ensures the callback is always
  // fresh when the saved places data changes.
  // ============================================

  const runProximityLookup = useCallback(
    async (
      coords: GeoCoords,
      fallbackAddress?: AddressData | null
    ): Promise<void> => {
      if (allSavedPlaces.length === 0) {
        // No saved places to match — just apply the address if provided
        if (fallbackAddress) {
          const addressString =
            formatAddressShort(fallbackAddress) ||
            fallbackAddress.displayName ||
            "";
          setState((prev) => ({
            ...prev,
            location: addressString,
            addressData: fallbackAddress,
            latitude: coords.latitude,
            longitude: coords.longitude,
          }));
          setTimeout(() => notifyChange(), 0);
        }
        return;
      }

      try {
        const result = await listByProximity({
          latitude: coords.latitude,
          longitude: coords.longitude,
        });

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const nearbyPlaces = Array.isArray(result.data)
            ? (result.data as SavedPlace[])
            : [];

          if (nearbyPlaces.length > 0) {
            const closest = nearbyPlaces[0];

            const closestAddressData: AddressData = {
              addressId: closest.id || "",
              displayName: closest.whereDone || closest.name || null,
              address1: closest.address1 || null,
              address2: closest.address2 || null,
              city: closest.city || null,
              country: closest.countryId || null,
              latitude: closest.latitude ?? null,
              longitude: closest.longitude ?? null,
              postalCode: closest.postalCode || null,
              region: closest.stateProvince || null,
            };

            setState((prev) => ({
              ...prev,
              savedPlaceId: closest.id || null,
              whereDone: closest.name || "",
              location:
                formatAddressShort(closestAddressData) ||
                closest.address1 ||
                "",
              addressData: closestAddressData,
              latitude: closest.latitude ?? null,
              longitude: closest.longitude ?? null,
              pointType: includePointType
                ? closest.placeType || prev.pointType
                : prev.pointType,
            }));

            setTimeout(() => notifyChange(), 0);
            return;
          }
        }
      } catch (err) {
        console.error("Proximity lookup failed:", err);

        // FIX 2: On lookup failure, apply the fallback address and explicitly
        // clear savedPlaceId / whereDone so stale values from a prior selection
        // don't remain silently associated with the new address coords.
        if (fallbackAddress) {
          const addressString =
            formatAddressShort(fallbackAddress) ||
            fallbackAddress.displayName ||
            "";
          setState((prev) => ({
            ...prev,
            savedPlaceId: null,
            whereDone: "",
            location: addressString,
            addressData: fallbackAddress,
            latitude: coords.latitude,
            longitude: coords.longitude,
          }));
          setTimeout(() => notifyChange(), 0);
        } else {
          // No fallback address either — clear any stale place association
          // so the form is not left in an inconsistent state.
          setState((prev) => ({
            ...prev,
            savedPlaceId: null,
            whereDone: "",
          }));
        }

        return;
      }

      // No saved place matched — apply fallback address if provided
      if (fallbackAddress) {
        const addressString =
          formatAddressShort(fallbackAddress) ||
          fallbackAddress.displayName ||
          "";
        setState((prev) => ({
          ...prev,
          location: addressString,
          addressData: fallbackAddress,
          latitude: coords.latitude,
          longitude: coords.longitude,
        }));
        setTimeout(() => notifyChange(), 0);
      }
    },
    // FIX 1: allSavedPlaces (full reference) instead of allSavedPlaces.length
    [allSavedPlaces, listByProximity, includePointType, notifyChange]
  );

  // ============================================
  // GPS auto-locate proximity lookup
  // (autoLocate: true path — triggered by useAutoLocation)
  // ============================================

  useEffect(() => {
    if (
      !isLocationReady ||
      !autoCoords ||
      !enabled ||
      !autoLocate ||
      hasAutoFilled.current ||
      hasProximityLookedUp.current
    ) {
      return;
    }

    hasProximityLookedUp.current = true;
    hasAutoFilled.current = true;

    runProximityLookup(autoCoords, autoAddress);
  }, [
    isLocationReady,
    autoCoords,
    autoAddress,
    enabled,
    autoLocate,
    runProximityLookup,
  ]);

  // ============================================
  // Initial coords proximity lookup
  // (autoLocate: false path — e.g. receipt scan)
  // ============================================

  useEffect(() => {
    if (
      !enabled ||
      autoLocate ||
      !initialCoords ||
      hasInitialCoordsProximityLookedUp.current ||
      allSavedPlaces.length === 0
    ) {
      return;
    }

    hasInitialCoordsProximityLookedUp.current = true;

    const lookupNearbyPlace = async () => {
      try {
        const result = await listByProximity({
          latitude: initialCoords.latitude,
          longitude: initialCoords.longitude,
        });

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const nearbyPlaces = Array.isArray(result.data)
            ? (result.data as SavedPlace[])
            : [];

          if (nearbyPlaces.length > 0) {
            const closest = nearbyPlaces[0];

            const closestAddressData: AddressData = {
              addressId: closest.id || "",
              displayName: closest.whereDone || closest.name || null,
              address1: closest.address1 || null,
              address2: closest.address2 || null,
              city: closest.city || null,
              country: closest.countryId || null,
              latitude: closest.latitude ?? null,
              longitude: closest.longitude ?? null,
              postalCode: closest.postalCode || null,
              region: closest.stateProvince || null,
            };

            setState((prev) => ({
              ...prev,
              savedPlaceId: closest.id || null,
              whereDone: prev.whereDone || closest.name || "",
              location:
                prev.location ||
                formatAddressShort(closestAddressData) ||
                closest.address1 ||
                "",
              addressData: prev.addressData || closestAddressData,
              latitude: prev.latitude ?? closest.latitude ?? null,
              longitude: prev.longitude ?? closest.longitude ?? null,
              pointType: includePointType
                ? closest.placeType || prev.pointType
                : prev.pointType,
            }));

            setTimeout(() => notifyChange(), 0);
          }
        }
      } catch (err) {
        console.error("Initial coords proximity lookup failed:", err);
      }
    };

    lookupNearbyPlace();
  }, [
    enabled,
    autoLocate,
    initialCoords,
    allSavedPlaces.length,
    listByProximity,
    includePointType,
    notifyChange,
  ]);

  // ============================================
  // Handlers
  // ============================================

  const handleWhereDoneChange = useCallback(
    (name: string, placeData: PlaceData | null) => {
      setState((prev) => {
        const newState: PlaceLocationState = {
          ...prev,
          whereDone: name,
          savedPlaceId: placeData?.savedPlaceId || null,
          savePlace: placeData?.savedPlaceId ? false : prev.savePlace,
          pointType:
            includePointType &&
              placeData?.savedPlaceId &&
              placeData?.address?.placeType
              ? placeData.address.placeType
              : prev.pointType,
        };

        if (placeData?.address) {
          const addressString = formatAddressShort(placeData.address);
          if (addressString) {
            newState.location = addressString;
          }
          newState.addressData = placeData.address;
          if (placeData.address.latitude != null) {
            newState.latitude = placeData.address.latitude;
          }
          if (placeData.address.longitude != null) {
            newState.longitude = placeData.address.longitude;
          }
          if (placeData.address.latitude && placeData.address.longitude) {
            setLocationBias({
              latitude: placeData.address.latitude,
              longitude: placeData.address.longitude,
            });
          }
        }

        return newState;
      });
      // FIX 5: Deferred with setTimeout(0) so notifyChange fires after setState
      // has committed rather than synchronously before the update is applied.
      // This prevents consumers from reading stale state in their onChange
      // callbacks — consistent with the project-wide stale closure pattern.
      setTimeout(() => notifyChange(), 0);
    },
    [notifyChange, includePointType]
  );

  const handleLocationChange = useCallback(
    (address1: string, fullAddress: AddressData | null) => {
      if (fullAddress?.latitude && fullAddress?.longitude) {
        // Confirmed address with coordinates — optimistically clear any stale
        // saved place association before the async proximity lookup resolves.
        //
        // FIX 2 (part 2): Without this pre-clear, if the user selects a new
        // address and the proximity lookup finds no match (or fails), the old
        // savedPlaceId / whereDone from a prior selection remain in state,
        // silently associating the new address with the wrong saved place.
        setState((prev) => ({
          ...prev,
          savedPlaceId: null,
          whereDone: "",
          location: address1,
          addressData: fullAddress,
          latitude: fullAddress.latitude,
          longitude: fullAddress.longitude,
        }));

        setLocationBias({
          latitude: fullAddress.latitude,
          longitude: fullAddress.longitude,
        });

        // runProximityLookup will overwrite state if a nearby saved place is
        // found, or leave the pre-cleared state intact if not.
        runProximityLookup(
          { latitude: fullAddress.latitude, longitude: fullAddress.longitude },
          fullAddress
        );
      } else {
        // Manual typing — clear coordinates and structured data
        setState((prev) => ({
          ...prev,
          location: address1,
          savedPlaceId: null,
          addressData: null,
          latitude: null,
          longitude: null,
        }));
        // FIX 5: Deferred for consistency — state is set above, notify after.
        setTimeout(() => notifyChange(), 0);
      }
    },
    [runProximityLookup, notifyChange]
  );

  const handleSavePlaceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setState((prev) => ({
        ...prev,
        savePlace: checked,
      }));
      // FIX 5: Deferred for consistency with the other handlers.
      setTimeout(() => notifyChange(), 0);
    },
    [notifyChange]
  );

  // FIX 3: Replaced dynamic `[name]: value` key with the explicit `pointType`
  // field. The signature follows the project's (name, value) => void convention
  // but PlaceLocationState has a fixed shape — a wrong `name` string would
  // silently set an unknown field with no TypeScript error. Since this handler
  // only ever sets pointType, use the literal key to be type-safe.
  const handlePointTypeChange = useCallback(
    (_name: string, value: PlaceTypeValue | null) => {
      setState((prev) => ({
        ...prev,
        pointType: value,
      }));
      // FIX 5: Deferred for consistency with the other handlers.
      setTimeout(() => notifyChange(), 0);
    },
    [notifyChange]
  );

  // ============================================
  // Computed
  // ============================================

  const canSavePlace = useMemo(() => {
    return !state.savedPlaceId && !!(state.whereDone || state.location);
  }, [state.savedPlaceId, state.whereDone, state.location]);

  // ============================================
  // getPlaceFields
  // ============================================

  const getPlaceFields = useCallback((): PlaceFieldsForInput => {
    const fields: PlaceFieldsForInput = {};

    if (state.whereDone) fields.whereDone = state.whereDone;
    if (state.location) fields.location = state.location;

    if (state.addressData) {
      const mapped = mapAddressToBackendFields(state.addressData);
      if (mapped.address1) fields.address1 = mapped.address1;
      if (mapped.address2) fields.address2 = mapped.address2;
      if (mapped.city) fields.city = mapped.city;
      if (mapped.stateProvince) fields.stateProvince = mapped.stateProvince;
      if (mapped.postalCode) fields.postalCode = mapped.postalCode;
      if (mapped.countryId) fields.countryId = mapped.countryId;
    }

    if (state.latitude != null) fields.latitude = state.latitude;
    if (state.longitude != null) fields.longitude = state.longitude;
    if (state.savedPlaceId) fields.savedPlaceId = state.savedPlaceId;
    if (state.savePlace) fields.savePlace = true;
    if (includePointType && state.pointType) fields.pointType = state.pointType;

    return fields;
  }, [state, includePointType]);

  // ============================================
  // Return
  // ============================================

  return useMemo(
    () => ({
      whereDone: state.whereDone,
      location: state.location,
      latitude: state.latitude,
      longitude: state.longitude,
      savedPlaceId: state.savedPlaceId,
      savePlace: state.savePlace,
      pointType: state.pointType,
      locationBias,
      savedPlacesAsPlaceData,
      // FIX 5 (redundant guard removed): useAutoLocation is called with
      // `enabled: enabled && autoLocate` so isLocationLoading is never true
      // when autoLocate is false. The `&& autoLocate` guard was unnecessary.
      isLocationLoading,
      canSavePlace,
      handleWhereDoneChange,
      handleLocationChange,
      handleSavePlaceChange,
      handlePointTypeChange,
      getPlaceFields,
      reset,
    }),
    [
      state,
      locationBias,
      savedPlacesAsPlaceData,
      isLocationLoading,
      canSavePlace,
      handleWhereDoneChange,
      handleLocationChange,
      handleSavePlaceChange,
      handlePointTypeChange,
      getPlaceFields,
      reset,
    ]
  );
};